/**
 * IEL — Índice de Efetividade da Liderança
 *
 * Camada superior sobre Disciplina e Conformidade (não as substitui).
 * Responde: "A rotina gerou resultado operacional?"
 *
 * IEL = (Disciplina × wD) + (Conformidade × wC) + (Resultado × wR)
 * Pesos padrão: 30% / 30% / 40% — parametrizáveis.
 */

import type { Indicator, IndicatorDirection, IndicatorSnapshot } from '../../../types/indicator';
import type { LstExecucao, LstTemplate } from '../types';
import {
  computeAchievementPct,
  computeExecutionResultScore,
  computeIndicatorResultScore,
  filterSnapshotsInPeriod,
} from './lstCorrelation';
import { computeTplOeeMetrics } from './lstOeeMetrics';
import {
  resolveTemplateIndicator,
  templateHasLinkedIndicator,
  templateIndicatorLabel,
  templatesWithIndicatorLink,
} from '../utils/lstIndicator';
import { toLocalISODate } from './lstService';

export interface IelWeights {
  disciplina: number;
  conformidade: number;
  resultado: number;
}

export const DEFAULT_IEL_WEIGHTS: IelWeights = {
  disciplina: 0.3,
  conformidade: 0.3,
  resultado: 0.4,
};

export type IelClassification = 'excelente' | 'bom' | 'atencao' | 'critico';

export const IEL_CLASSIFICATION_LABELS: Record<IelClassification, string> = {
  excelente: 'Excelência',
  bom: 'Bom',
  atencao: 'Atenção',
  critico: 'Crítico',
};

export interface LinkedIndicatorAssessment {
  /** Identificador estável para UI */
  key: string;
  name: string;
  routineTitle: string | null;
  target: number | null;
  current: number | null;
  /** Diferença Atual − Meta (pontos percentuais quando aplicável). */
  gap: number | null;
  unit: string;
  direction: IndicatorDirection;
  score: number | null;
  classification: IelClassification | null;
  trend: number | null;
  source: 'execution' | 'snapshots' | 'current_value' | 'none';
}

export interface IelModel {
  score: number;
  classification: IelClassification;
  classificationLabel: string;
  disciplina: number;
  conformidade: number;
  resultado: number | null;
  weights: IelWeights;
  trend: number | null;
  diagnosis: string[];
  linkedIndicators: LinkedIndicatorAssessment[];
  resultadoAvailable: boolean;
}

export interface ComputeIelInput {
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  dates: string[];
  /** Período anterior para tendência (opcional). */
  previousDates?: string[];
  indicators?: Indicator[];
  indicatorSnapshots?: IndicatorSnapshot[];
  /** Filtra execuções por líder (reunião de camada / auditoria). */
  userId?: string;
  weights?: IelWeights;
  /** Evita recursão ao calcular tendência do período anterior. */
  includeTrend?: boolean;
}

export function classifyIelScore(score: number): IelClassification {
  if (score >= 81) return 'excelente';
  if (score >= 61) return 'bom';
  if (score >= 41) return 'atencao';
  return 'critico';
}

export function computeIndicatorGap(
  current: number,
  target: number,
  direction: IndicatorDirection,
): number {
  if (direction === 'lower_is_better') {
    return Math.round((target - current) * 100) / 100;
  }
  return Math.round((current - target) * 100) / 100;
}

function previousPeriodDates(dates: string[]): string[] {
  if (dates.length === 0) return [];
  const [y, m, d] = dates[0].split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setDate(start.getDate() - dates.length);
  const out: string[] = [];
  for (let i = 0; i < dates.length; i++) {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    out.push(toLocalISODate(dt));
  }
  return out;
}

function filterExecForUser(execucoes: LstExecucao[], userId?: string): LstExecucao[] {
  if (!userId) return execucoes;
  return execucoes.filter((e) => e.user_id === userId);
}

function aggregateResultadoScore(assessments: LinkedIndicatorAssessment[]): number | null {
  const valid = assessments.filter((a) => a.score != null);
  if (valid.length === 0) return null;
  return Math.round(valid.reduce((s, a) => s + (a.score as number), 0) / valid.length);
}

function buildExecutionAssessment(
  template: LstTemplate,
  execucoes: LstExecucao[],
  indicators: Indicator[],
  periodStart: string,
  periodEnd: string,
  prevStart: string,
  prevEnd: string,
): LinkedIndicatorAssessment | null {
  const meta = resolveTemplateIndicator(template, indicators);
  if (!meta.hasLink || meta.target == null) return null;

  const name = meta.name ?? templateIndicatorLabel(template, indicators) ?? 'Indicador';
  const inPeriod = execucoes.filter(
    (e) =>
      e.template_id === template.id &&
      e.data >= periodStart &&
      e.data <= periodEnd &&
      e.indicator_realized_value != null &&
      Number.isFinite(e.indicator_realized_value),
  );

  let current: number | null = null;
  let source: LinkedIndicatorAssessment['source'] = 'none';

  if (inPeriod.length > 0) {
    current =
      inPeriod.reduce((s, e) => s + (e.indicator_realized_value as number), 0) / inPeriod.length;
    source = 'execution';
  }

  const prevPeriod = execucoes.filter(
    (e) =>
      e.template_id === template.id &&
      e.data >= prevStart &&
      e.data <= prevEnd &&
      e.indicator_realized_value != null &&
      Number.isFinite(e.indicator_realized_value),
  );
  let prevCurrent: number | null = null;
  if (prevPeriod.length > 0) {
    prevCurrent =
      prevPeriod.reduce((s, e) => s + (e.indicator_realized_value as number), 0) / prevPeriod.length;
  }

  if (current == null && meta.globalId) {
    const ind = indicators.find((i) => i.id === meta.globalId);
    if (ind) {
      const scoreResult = computeIndicatorResultScore(
        ind,
        [],
        periodStart,
        periodEnd,
      );
      if (scoreResult.realized != null) {
        current = scoreResult.realized;
        source = scoreResult.source === 'none' ? 'none' : scoreResult.source;
      }
    }
  }

  if (current == null) return null;

  const score = computeAchievementPct(current, meta.target, meta.direction);
  const prevScore =
    prevCurrent != null
      ? computeAchievementPct(prevCurrent, meta.target, meta.direction)
      : null;

  return {
    key: `tpl-${template.id}`,
    name,
    routineTitle: template.title,
    target: meta.target,
    current: Math.round(current * 100) / 100,
    gap: computeIndicatorGap(current, meta.target, meta.direction),
    unit: meta.unit,
    direction: meta.direction,
    score,
    classification: classifyIelScore(score),
    trend: prevScore != null ? score - prevScore : null,
    source,
  };
}

function buildGlobalIndicatorAssessment(
  indicator: Indicator,
  templates: LstTemplate[],
  snapshots: IndicatorSnapshot[],
  periodStart: string,
  periodEnd: string,
  prevStart: string,
  prevEnd: string,
): LinkedIndicatorAssessment {
  const linkedTpl = templates.find((t) => t.linked_indicator_id === indicator.id);
  const scoreResult = computeIndicatorResultScore(indicator, snapshots, periodStart, periodEnd);
  const prevResult = computeIndicatorResultScore(indicator, snapshots, prevStart, prevEnd);

  const current = scoreResult.realized;
  const target = scoreResult.target ?? indicator.target_value;
  const score = scoreResult.achievementPct;
  const prevScore = prevResult.achievementPct;

  return {
    key: `ind-${indicator.id}`,
    name: indicator.name,
    routineTitle: linkedTpl?.title ?? null,
    target,
    current,
    gap:
      current != null && target != null
        ? computeIndicatorGap(current, target, indicator.direction)
        : null,
    unit: indicator.unit ?? scoreResult.unit,
    direction: indicator.direction,
    score,
    classification: score != null ? classifyIelScore(score) : null,
    trend: score != null && prevScore != null ? score - prevScore : null,
    source: scoreResult.source === 'none' ? 'none' : scoreResult.source,
  };
}

export function collectLinkedIndicatorAssessments(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  indicators: Indicator[],
  snapshots: IndicatorSnapshot[],
  periodStart: string,
  periodEnd: string,
  prevStart: string,
  prevEnd: string,
): LinkedIndicatorAssessment[] {
  const linkedTemplates = templatesWithIndicatorLink(templates.filter((t) => t.active));
  const assessments: LinkedIndicatorAssessment[] = [];
  const seenGlobal = new Set<string>();

  for (const tpl of linkedTemplates) {
    if (tpl.linked_indicator_id) {
      if (seenGlobal.has(tpl.linked_indicator_id)) continue;
      seenGlobal.add(tpl.linked_indicator_id);
      const ind = indicators.find((i) => i.id === tpl.linked_indicator_id);
      if (ind) {
        const indSnaps = snapshots.filter((s) => s.indicator_id === ind.id);
        assessments.push(
          buildGlobalIndicatorAssessment(
            ind,
            linkedTemplates,
            indSnaps,
            periodStart,
            periodEnd,
            prevStart,
            prevEnd,
          ),
        );
        continue;
      }
    }
    const custom = buildExecutionAssessment(
      tpl,
      execucoes,
      indicators,
      periodStart,
      periodEnd,
      prevStart,
      prevEnd,
    );
    if (custom) assessments.push(custom);
  }

  return assessments;
}

export function buildIelDiagnosis(model: Pick<IelModel, 'disciplina' | 'conformidade' | 'resultado' | 'linkedIndicators'>): string[] {
  const lines: string[] = [];
  const { disciplina, conformidade, resultado } = model;

  if (resultado == null) {
    lines.push(
      'Resultado operacional indisponível — vincule indicadores às rotinas e registre medições.',
    );
    return lines;
  }

  if (disciplina >= 80 && conformidade >= 80 && resultado < 61) {
    lines.push('Alta disciplina e conformidade, porém baixo impacto nos indicadores.');
    lines.push(
      'As rotinas estão sendo executadas corretamente, mas não estão produzindo os resultados esperados.',
    );
  } else if (disciplina >= 75 && resultado >= 81) {
    lines.push('Resultado operacional elevado sustentado por alta disciplina da liderança.');
  } else if (disciplina < 60 && conformidade < 60) {
    lines.push('Disciplina e conformidade críticas — priorize presença e execução correta antes de escalar metas.');
  } else if (disciplina >= 80 && conformidade < 60) {
    lines.push('Disciplina elevada, porém conformidade abaixo da meta — foco em qualidade da execução.');
  } else if (resultado >= 81 && conformidade < 70) {
    lines.push('Indicadores fortes apesar de conformidade irregular — investigar variabilidade do processo.');
  } else {
    lines.push('IEL equilibrado — sustente gemba, tratamento de desvios e acompanhamento de indicadores.');
  }

  const critical = model.linkedIndicators.filter((a) => a.classification === 'critico');
  if (critical.length > 0) {
    lines.push(
      `Indicador(es) crítico(s): ${critical.map((c) => c.name).join(', ')}.`,
    );
  }

  return lines;
}

export function computeIel(input: ComputeIelInput): IelModel {
  const {
    templates,
    execucoes,
    dates,
    previousDates,
    indicators = [],
    indicatorSnapshots = [],
    userId,
    weights = DEFAULT_IEL_WEIGHTS,
    includeTrend = true,
  } = input;

  const activeTemplates = templates.filter((t) => t.active);
  const scopedExec = filterExecForUser(execucoes, userId);
  const prevDates =
    previousDates ?? (includeTrend ? previousPeriodDates(dates) : []);

  const oee = computeTplOeeMetrics(activeTemplates, scopedExec, dates);
  const periodStart = dates[0] ?? toLocalISODate(new Date());
  const periodEnd = dates[dates.length - 1] ?? periodStart;
  const prevStart = prevDates[0] ?? periodStart;
  const prevEnd = prevDates[prevDates.length - 1] ?? periodEnd;

  let linkedIndicators = collectLinkedIndicatorAssessments(
    activeTemplates,
    scopedExec,
    indicators,
    indicatorSnapshots,
    periodStart,
    periodEnd,
    prevStart,
    prevEnd,
  );

  if (linkedIndicators.length === 0 && userId) {
    const execScore = computeExecutionResultScore(
      activeTemplates,
      scopedExec,
      indicators,
      userId,
      periodStart,
      periodEnd,
    );
    if (execScore?.achievementPct != null) {
      linkedIndicators = [
        {
          key: 'exec-aggregate',
          name: 'Resultado das rotinas',
          routineTitle: null,
          target: execScore.target,
          current: execScore.realized,
          gap:
            execScore.realized != null && execScore.target != null
              ? computeIndicatorGap(execScore.realized, execScore.target, execScore.direction)
              : null,
          unit: execScore.unit,
          direction: execScore.direction,
          score: execScore.achievementPct,
          classification: classifyIelScore(execScore.achievementPct),
          trend: null,
          source: 'execution',
        },
      ];
    }
  }

  const resultado = aggregateResultadoScore(linkedIndicators);
  const resultadoAvailable = resultado != null;

  let weightSum = weights.disciplina + weights.conformidade;
  let weighted = oee.disciplinaPct * weights.disciplina + oee.conformidadePct * weights.conformidade;

  if (resultadoAvailable) {
    weightSum += weights.resultado;
    weighted += resultado * weights.resultado;
  }

  const score = Math.round(weighted / weightSum);
  const classification = classifyIelScore(score);

  let trend: number | null = null;
  if (includeTrend && prevDates.length > 0) {
    const prev = computeIel({
      ...input,
      dates: prevDates,
      previousDates: [],
      includeTrend: false,
    });
    trend = score - prev.score;
  }

  const model: IelModel = {
    score,
    classification,
    classificationLabel: IEL_CLASSIFICATION_LABELS[classification],
    disciplina: oee.disciplinaPct,
    conformidade: oee.conformidadePct,
    resultado,
    weights,
    trend,
    diagnosis: [],
    linkedIndicators,
    resultadoAvailable,
  };

  model.diagnosis = buildIelDiagnosis(model);
  return model;
}

/** Avaliação de um único template/rotina para exibição no drawer. */
export function assessRoutineIndicator(
  template: LstTemplate,
  execucoes: LstExecucao[],
  indicators: Indicator[],
  snapshots: IndicatorSnapshot[],
  periodStart: string,
  periodEnd: string,
): LinkedIndicatorAssessment | null {
  if (!templateHasLinkedIndicator(template)) return null;

  if (template.linked_indicator_id) {
    const ind = indicators.find((i) => i.id === template.linked_indicator_id);
    if (ind) {
      const indSnaps = filterSnapshotsInPeriod(
        snapshots.filter((s) => s.indicator_id === ind.id),
        periodStart,
        periodEnd,
      );
      const prevLen = Math.max(1, Math.ceil((new Date(periodEnd).getTime() - new Date(periodStart).getTime()) / 86400000));
      const [y, m, d] = periodStart.split('-').map(Number);
      const prevEndDt = new Date(y, m - 1, d);
      prevEndDt.setDate(prevEndDt.getDate() - 1);
      const prevStartDt = new Date(prevEndDt);
      prevStartDt.setDate(prevStartDt.getDate() - prevLen + 1);
      return buildGlobalIndicatorAssessment(
        ind,
        [template],
        snapshots.filter((s) => s.indicator_id === ind.id),
        periodStart,
        periodEnd,
        toLocalISODate(prevStartDt),
        toLocalISODate(prevEndDt),
      );
    }
  }

  return buildExecutionAssessment(
    template,
    execucoes,
    indicators,
    periodStart,
    periodEnd,
    periodStart,
    periodEnd,
  );
}
